import { PageRequest } from '@/components/tasks-kanban/adapter';
import { SortColumnRequest, TeammateModel, ZmkTaskByPerformerListRequest } from '../models';
import { TaskSortingModel } from '@/pages/employee-tasks-page/models';
import { TasksOrderPagesAdapterService } from './TasksOrderPagesAdapterService';
import { ref, watch } from 'vue';
import { computed } from 'vue';
import ZmkTaskPerformerModel from '../models/ZmkTaskPerformerModel';
import { TasksKanbanFilterModel } from '@/components/tasks-kanban/models';
import TasksKanbanFilter from '@/components/tasks-kanban/components/TasksKanbanFilter.vue';

export function useTaskKanbanFilter() {
  /**API сервис */
  const apiService = new TasksOrderPagesAdapterService();
  /**Команда */
  const team = ref<TeammateModel[]>([]);

  /**Выбранные сотрудники для отображения */
  const selectedEmployees = ref<number[]>([]);

  /**Список задач исполнителя */
  const performerTaskList = ref<ZmkTaskPerformerModel[]>([]);

  /**Фильтр */
  const filter = ref(new TasksKanbanFilterModel());

  /**ID задач */
  const tasksIds = ref<number[]>([]);

  const zmkTaskByPerformerListRequest = ref<ZmkTaskByPerformerListRequest>(
    new ZmkTaskByPerformerListRequest(),
  );
  const $tasksKanbanFilter = ref<InstanceType<typeof TasksKanbanFilter>>();

  /**Отображаемая команда */
  const viewTeam = computed(() =>
    selectedEmployees.value.filter((x) => !!x).length
      ? team.value.filter((user) => selectedEmployees.value.includes(user.Id))
      : team.value,
  );

  /**Обновить фильтр и список задач zmk */
  const updateListData = async (
    pageRequest: PageRequest,
    clear?: boolean,
    filterRequest?: ZmkTaskByPerformerListRequest,
    sortData?: SortColumnRequest,
  ) => {
    if (clear) performerTaskList.value = [];
    zmkTaskByPerformerListRequest.value = new ZmkTaskByPerformerListRequest({
      GlobalStatusIds: getIds(
        $tasksKanbanFilter.value.selectedOptionsFilter.GlobalStatuses,
        $tasksKanbanFilter.value.filterOptions.GlobalStatuses,
      ).map(String),
      DifficultyIds: filterRequest?.DifficultyIds,
      NamespaceId: filterRequest?.NamespaceId,
      QueueIds: filterRequest?.QueueIds,
      TagIds: filterRequest?.TagIds,
      TypeIds: filterRequest?.TypeIds,
      ProjectId: filterRequest?.ProjectId ? filterRequest?.ProjectId : undefined,
      StatusIds: filterRequest?.StatusIds,
      PerformerIds: !selectedEmployees.value.length ? [] : viewTeam.value.map((user) => user.Id),
      Search: filter.value.Search,
      TaskPage: pageRequest,
      Sort: sortData,
    });
    await updateData();
  };

  /** обновить список задач zmk */
  const updateData = async () => {
    const result = await apiService.getZmkTaskByPerformerList(zmkTaskByPerformerListRequest.value);
    if (result.IsSuccess) {
      performerTaskList.value = result.Value.TaskPerformerList;
      tasksIds.value = result.Value.TaskPerformerList.flatMap((res) =>
        res.TaskList.map((task) => task.Id),
      );
    }
  };

  /** обновить список задач для исполнителя */
  const updateDataSinglePerformer = async () => {
    const result = await apiService.getZmkTaskByPerformerList(zmkTaskByPerformerListRequest.value);
    if (result.IsSuccess) {
      let f = performerTaskList.value.find(
        (x) => x.Performer.Id === zmkTaskByPerformerListRequest.value.PerformerIds[0],
      );
      f.TaskList = [];
      f.PageStep = result.Value.TaskPerformerList[0].PageStep;
      f.TaskList = result.Value.TaskPerformerList[0].TaskList;
      f.TaskPage = result.Value.TaskPerformerList[0].TaskPage;
      f.Performer = result.Value.TaskPerformerList[0].Performer;
      tasksIds.value = performerTaskList.value.flatMap((res) =>
        res.TaskList.map((task) => task.Id),
      );
    }
  };
  /**
   * обновить задачи по одному исполнителю и ли для всех исполнителей с использованием фильтра
   * @param performerId
   */
  const onUpdateTask = async (performerId?: number) => {
    zmkTaskByPerformerListRequest.value.PerformerIds = performerId ? [performerId] : [];
    await updateDataSinglePerformer();
  };
  /**
   * обновить сортировку задач по одному исполнителю
   * @param performerId
   */
  const onUpdateSortedTasks = async (performerId: number, sortModel: TaskSortingModel) => {
    // 1. Найдём активный ключ (например, OrderByName)

    const activeEntry = Object.entries(sortModel).find(([_, value]) => value !== undefined);

    if (!activeEntry) return;

    const [orderByKey, currentValue] = activeEntry; // Например: "OrderByName", true

    // 2. Преобразуем OrderByName → SortByTaskName
    const sortKey = orderByKey
      .replace('OrderById', 'SortByTaskId')
      .replace('OrderByQueue', 'SortByQueueName')
      .replace('OrderByName', 'SortByTaskName')
      .replace('OrderByDifficulty', 'SortByDifficultyName')
      .replace('OrderByEstimateTime', 'SortByEstimateTime')
      .replace('OrderByPriority', 'SortByPriorityName')
      .replace('OrderByStatus', 'SortByStatusName')
      .replace('OrderByWorkTime', 'SortByWorkTime') as keyof SortColumnRequest;

    // 3. Переключаем выбранное поле, остальные сбрасываем
    const newSort: SortColumnRequest = {
      SortByTaskId: undefined,
      SortByQueueName: undefined,
      SortByTaskName: undefined,
      SortByDifficultyName: undefined,
      SortByEstimateTime: undefined,
      SortByPriorityName: undefined,
      SortByStatusName: undefined,
      SortByWorkTime: undefined,
    };

    const previousValue = zmkTaskByPerformerListRequest.value.Sort?.[sortKey];
    newSort[sortKey] = previousValue === true ? false : true;

    // 4. Применяем сортировку
    zmkTaskByPerformerListRequest.value.Sort = new SortColumnRequest(newSort);

    // 5. Загружаем задачи
    await onUpdateTask(performerId);
  };
  const loadZmkTaskByPerformerListRequestLocalstore = () => {
    const data = localStorage.getItem('zmkTaskByPerformerListRequest');
    if (data) {
      try {
        const zmkTaskByPerformerListRequestData = JSON.parse(data);
        if (zmkTaskByPerformerListRequestData) {
          zmkTaskByPerformerListRequest.value = zmkTaskByPerformerListRequestData;
        }
      } catch (e) {
        console.error(e);
      }
    }
  };
  const saveZmkTaskByPerformerListRequestLocalstore = () => {
    localStorage.setItem(
      'zmkTaskByPerformerListRequest',
      JSON.stringify(zmkTaskByPerformerListRequest.value),
    );
  };

  const getIds = <T extends { Id: string | number }>(items: T[], fullList: T[]) => {
    return items?.length === fullList?.length ? [] : items.map((i) => i.Id);
  };

  return {
    team,
    selectedEmployees,
    performerTaskList,
    filter,
    tasksIds,
    viewTeam,
    $tasksKanbanFilter,
    zmkTaskByPerformerListRequest,
    getIds,
    onUpdateTask,
    updateListData,
    onUpdateSortedTasks,
    updateData,
    updateDataSinglePerformer,
    loadZmkTaskByPerformerListRequestLocalstore,
    saveZmkTaskByPerformerListRequestLocalstore,
  };
}
